import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { useToast } from '@chakra-ui/react';
import { useHistory } from 'react-router';
import {
  getDebitNoteConfiguration,
  getSeriesNumber,
  getSinisterById,
  getSinisterDashboard,
  getSinisterDocumentsById,
  getSinisterLogById,
  getSinistersInsurers,
  getSinistersInsurersAsSelect,
  getSinistersIssuer,
  getSinistersIssuersAsSelect,
  getSinistersList,
  getSinistersTypeAsSelect,
  getSinistersTypes,
} from '~/services/Sinisters';
import {
  SeriesNumber,
  SinisterDebitNoteConfiguration,
  SinisterDocuments,
  SinistersDashboard,
  SinistersInsurers,
  SinistersIssuers,
  SinistersLog,
  SinistersType,
  SinistersView,
} from '~/types/sinisters';
import { Pagination, SelectItem } from '~/types';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export function useSinistersTypes(
  options: UseQueryOptions<SinistersType[], unknown> = {},
): UseQueryResult<SinistersType[], unknown> {
  return useQuery<SinistersType[], unknown>(
    ['sinistersTypeAux'],
    () => getSinistersTypes(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useSinistersTypesAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
): UseQueryResult<SelectItem[], unknown> {
  return useQuery<SelectItem[], unknown>(
    ['sinistersType'],
    () => getSinistersTypeAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useSinistersList(
  page: number,
  search: string,
  status: string,
  orderby = 'numero_serie',
  query: QueryFilterContext,
  pageSize: number,
  options: UseQueryOptions<Pagination<SinistersView>, unknown> = {},
): UseQueryResult<Pagination<SinistersView>, unknown> {
  const arrGroups = query.groups?.map(e => e.value);
  const arrHeadOffices = query.headOffices?.map(e => e.value);
  const arrBrancches = query.branches?.map(e => e.value);
  return useQuery<Pagination<SinistersView>, unknown>(
    [
      'sinistersList',
      page,
      search,
      status,
      orderby,
      { arrGroups, arrHeadOffices, arrBrancches },
      pageSize,
    ],
    () =>
      getSinistersList(
        page,
        search,
        status,
        orderby,
        {
          groups: arrGroups,
          headoffices: arrHeadOffices,
          branches: arrBrancches,
        },
        pageSize,
      ),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useSeriesNumber(
  search = '',
  options: UseQueryOptions<Pagination<SeriesNumber>, unknown> = {},
  query?: QueryFilterContext,
): UseQueryResult<Pagination<SeriesNumber>, unknown> {
  const toast = useToast();
  const arrGroups = query?.groups?.map(e => e.value);
  const arrHeadOffices = query?.headOffices?.map(e => e.value);
  const arrBrancches = query?.branches?.map(e => e.value);
  return useQuery<Pagination<SeriesNumber>, unknown>(
    ['nmr_serie', search, { arrGroups, arrHeadOffices, arrBrancches }],
    () =>
      getSeriesNumber(search, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar números de série, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useSinister(
  id?: number,
  options: UseQueryOptions<SinistersView, unknown> = {},
): UseQueryResult<SinistersView> {
  const toast = useToast();
  const history = useHistory();

  return useQuery<SinistersView, unknown>(
    ['sinister', id],
    () => getSinisterById(id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar detalhes do sinistro',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      },
    },
  );
}

export function useSinisterDashboard(
  query: QueryFilterContext,
  options: UseQueryOptions<SinistersDashboard, unknown> = {},
): UseQueryResult<SinistersDashboard> {
  const arrGroups = query.groups?.map(e => e.value);
  const arrHeadOffices = query.headOffices?.map(e => e.value);
  const arrBrancches = query.branches?.map(e => e.value);
  const toast = useToast();
  const history = useHistory();

  return useQuery<SinistersDashboard, unknown>(
    ['sinisterDashboard', { arrGroups, arrHeadOffices, arrBrancches }],
    () =>
      getSinisterDashboard({
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao buscar informações do dashboard de sinistro',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      },
    },
  );
}

export function useSinisterLog(
  id?: number,
  options: UseQueryOptions<SinistersLog[], unknown> = {},
): UseQueryResult<SinistersLog[]> {
  const toast = useToast();
  const history = useHistory();

  return useQuery<SinistersLog[], unknown>(
    ['sinisterLog', id],
    () => getSinisterLogById(id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar detalhes do sinistro',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      },
    },
  );
}

export function useSinistersInsurers(
  options: UseQueryOptions<SinistersInsurers[], unknown> = {},
): UseQueryResult<SinistersInsurers[], unknown> {
  return useQuery<SinistersInsurers[], unknown>(
    ['sinistersInsurers'],
    () => getSinistersInsurers(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useSinistersInsurersAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
): UseQueryResult<SelectItem[], unknown> {
  return useQuery<SelectItem[], unknown>(
    ['sinistersInsurersAsSelect'],
    () => getSinistersInsurersAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useSinistersIssuer(
  options: UseQueryOptions<SinistersIssuers[], unknown> = {},
): UseQueryResult<SinistersIssuers[], unknown> {
  return useQuery<SinistersIssuers[], unknown>(
    ['sinistersIssuers'],
    () => getSinistersIssuer(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useSinistersIssuerAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
): UseQueryResult<SelectItem[], unknown> {
  return useQuery<SelectItem[], unknown>(
    ['sinistersIssuersAsSelect'],
    () => getSinistersIssuersAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useDebitNoteConfiguration(
  options: UseQueryOptions<SinisterDebitNoteConfiguration, unknown> = {},
): UseQueryResult<SinisterDebitNoteConfiguration> {
  const toast = useToast();
  const history = useHistory();

  return useQuery<SinisterDebitNoteConfiguration, unknown>(
    ['sinisterDebitNoteConfiguration'],
    () => getDebitNoteConfiguration(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useSinisterDocuments(
  id?: number,
  options: UseQueryOptions<SinisterDocuments[], unknown> = {},
): UseQueryResult<SinisterDocuments[]> {
  const toast = useToast();
  const history = useHistory();

  return useQuery<SinisterDocuments[], unknown>(
    ['sinisterDocuments', id],
    () => getSinisterDocumentsById(id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar documentos do sinistro',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      },
    },
  );
}
